import { useMemo } from 'react'
import { useHubData } from './useHubData'

export type HubType = 'sales' | 'purchase' | 'identification' | 'disbursement' | 'irs'

export interface HubAccess {
  canAccess: boolean
  isComplete: boolean
  completionPercent: number
  message?: string
}

export const useHubAccess = (hubType: HubType, user?: { firstName?: string; lastName?: string; address?: { street?: string; city?: string; state?: string; zipCode?: string }; ssn?: string; id?: string; isIdentityVerified?: boolean } | null, isIdentityVerified?: boolean) => {
  const salesData = useHubData('phase1', user?.id)
  const purchaseData = useHubData('acquisition', user?.id)
  const exchangeData = useHubData('exchange', user?.id)

  const hubAccess = useMemo<HubAccess>(() => {
    const isSalesComplete = () => {
      const salesHubSubmitted = typeof window !== 'undefined' && localStorage.getItem('sales_hub_submitted') === 'true'
      if (!salesHubSubmitted) return false
      const data = salesData.data
      return !!(
        data?.exchangerPhaseOne?.names &&
        data?.exchangerPhaseOne?.homeAddress &&
        data?.exchangerPhaseOne?.taxIdNumber &&
        data?.buyer?.names &&
        data?.closingCompanySale?.name &&
        data?.closingCompanySale?.address &&
        data?.relinquished?.addressOrLegalDescription
      )
    }

    const isPurchaseComplete = () => {
      const purchaseHubSubmitted = typeof window !== 'undefined' && localStorage.getItem('purchase_hub_submitted') === 'true'
      if (!purchaseHubSubmitted) return false
      const data = purchaseData.data
      return !!(
        data?.exchangerPhaseTwo?.names &&
        data?.seller?.names &&
        data?.closingCompanyPurchase?.name &&
        data?.closingCompanyPurchase?.address &&
        data?.replacement?.addressOrLegalDescription
      )
    }

    const isIdentificationComplete = () => {
      const identificationHubSubmitted = typeof window !== 'undefined' && localStorage.getItem('identification_hub_submitted') === 'true'
      if (!identificationHubSubmitted) return false
      const data = exchangeData.data || salesData.data
      return !!(
        data?.identificationForm?.exchangerNames &&
        data?.identificationForm?.relinquishedPropertyAddress &&
        data?.identificationForm?.idRule &&
        data?.identificationForm?.identifiedProperties?.length > 0
      )
    }

    const isDisbursementComplete = () => {
      const disbursementHubSubmitted = typeof window !== 'undefined' && localStorage.getItem('disbursement_hub_submitted') === 'true'
      if (!disbursementHubSubmitted) return false
      const data = purchaseData.data
      return !!(
        data?.disbursementForm?.exchangerNames &&
        data?.disbursementForm?.amount &&
        data?.disbursementForm?.wireRecipient &&
        data?.disbursementForm?.wireRecipientAddress &&
        data?.disbursementForm?.wireRecipientBankName &&
        data?.disbursementForm?.wireRecipientBankABA &&
        data?.disbursementForm?.wireRecipientBankAccount
      )
    }

    const getCompletionPercent = (hubType: HubType): number => {
      switch (hubType) {
        case 'sales':
          const salesHubSubmitted = typeof window !== 'undefined' && localStorage.getItem('sales_hub_submitted') === 'true'
          if (salesHubSubmitted) {
            return 100
          }
          const getUserFullName = () => {
            if (user?.firstName && user?.lastName) {
              return `${user.firstName} ${user.lastName}`
            }
            return ''
          }
          const getUserHomeAddress = () => {
            if (user?.address) {
              const addr = user.address
              const parts = [addr.street, addr.city, addr.state, addr.zipCode].filter(Boolean)
              return parts.join(', ')
            }
            return ''
          }
          const exchangerNames = salesData.data?.exchangerPhaseOne?.names || getUserFullName()
          const exchangerHomeAddress = salesData.data?.exchangerPhaseOne?.homeAddress || getUserHomeAddress()
          const exchangerTaxId = salesData.data?.exchangerPhaseOne?.taxIdNumber || user?.ssn || ''
          const salesFields = [
            exchangerNames,
            exchangerHomeAddress,
            exchangerTaxId,
            salesData.data?.buyer?.names,
            salesData.data?.closingCompanySale?.name,
            salesData.data?.closingCompanySale?.address,
            salesData.data?.closingCompanySale?.phone,
            salesData.data?.closingCompanySale?.closerName,
            salesData.data?.closingCompanySale?.closerEmail,
            salesData.data?.relinquished?.addressOrLegalDescription
          ]
          const salesDocuments = salesData.data?.documents || []
          const salesPurchaseAgreementUploaded = salesDocuments.some(doc => doc.name === 'Sales & Purchase Agreement' && doc.uploaded)
          const preliminaryTitleReportUploaded = salesDocuments.some(doc => doc.name === 'Preliminary Title Report' && doc.uploaded)
          const salesFilled = salesFields.filter(Boolean).length + (salesPurchaseAgreementUploaded ? 1 : 0) + (preliminaryTitleReportUploaded ? 1 : 0)
          const totalFields = salesFields.length + 2
          return Math.round((salesFilled / totalFields) * 100)
        
        case 'purchase':
          const purchaseHubSubmitted = typeof window !== 'undefined' && localStorage.getItem('purchase_hub_submitted') === 'true'
          if (purchaseHubSubmitted) {
            return 100
          }
          const purchaseFields = [
            purchaseData.data?.exchangerPhaseTwo?.names,
            purchaseData.data?.seller?.names,
            purchaseData.data?.closingCompanyPurchase?.name,
            purchaseData.data?.closingCompanyPurchase?.address,
            purchaseData.data?.replacement?.addressOrLegalDescription
          ]
          const purchaseFilled = purchaseFields.filter(Boolean).length
          return Math.round((purchaseFilled / purchaseFields.length) * 100)
        
        case 'identification':
          const identificationHubSubmitted = typeof window !== 'undefined' && localStorage.getItem('identification_hub_submitted') === 'true'
          if (identificationHubSubmitted) {
            return 100
          }
          const idProperties = exchangeData.data?.identificationForm?.identifiedProperties || salesData.data?.identificationForm?.identifiedProperties
          const idFields = [
            exchangeData.data?.identificationForm?.exchangerNames || salesData.data?.identificationForm?.exchangerNames,
            exchangeData.data?.identificationForm?.relinquishedPropertyAddress || salesData.data?.identificationForm?.relinquishedPropertyAddress,
            exchangeData.data?.identificationForm?.idRule || salesData.data?.identificationForm?.idRule,
            (idProperties?.length ?? 0) > 0
          ]
          const idFilled = idFields.filter(Boolean).length
          return Math.round((idFilled / idFields.length) * 100)
        
        case 'disbursement':
          const disbursementHubSubmitted = typeof window !== 'undefined' && localStorage.getItem('disbursement_hub_submitted') === 'true'
          if (disbursementHubSubmitted) {
            return 100
          }
          const disbFields = [
            purchaseData.data?.disbursementForm?.exchangerNames,
            purchaseData.data?.disbursementForm?.amount,
            purchaseData.data?.disbursementForm?.wireRecipient,
            purchaseData.data?.disbursementForm?.wireRecipientAddress,
            purchaseData.data?.disbursementForm?.wireRecipientBankName,
            purchaseData.data?.disbursementForm?.wireRecipientBankABA,
            purchaseData.data?.disbursementForm?.wireRecipientBankAccount
          ]
          const disbFilled = disbFields.filter(Boolean).length
          return Math.round((disbFilled / disbFields.length) * 100)
        
        default:
          return 0
      }
    }

    switch (hubType) {
      case 'sales':
        const userIsVerified = isIdentityVerified !== undefined ? isIdentityVerified : user?.isIdentityVerified
        return {
          canAccess: userIsVerified === true,
          isComplete: isSalesComplete(),
          completionPercent: userIsVerified === true ? getCompletionPercent('sales') : 0,
          message: userIsVerified === true ? undefined : 'Please verify your identity first'
        }
      
      case 'identification':
        const salesDoneForId = isSalesComplete()
        return {
          canAccess: salesDoneForId,
          isComplete: isIdentificationComplete(),
          completionPercent: getCompletionPercent('identification'),
          message: salesDoneForId ? undefined : 'Please complete the Sales Hub first'
        }
      
      case 'purchase':
        const idDoneForPurchase = isIdentificationComplete()
        return {
          canAccess: idDoneForPurchase,
          isComplete: isPurchaseComplete(),
          completionPercent: idDoneForPurchase ? getCompletionPercent('purchase') : 0,
          message: idDoneForPurchase ? undefined : 'Please complete the Identification Hub first'
        }
      
      case 'disbursement':
        const purchaseDoneForDisb = isPurchaseComplete()
        return {
          canAccess: purchaseDoneForDisb,
          isComplete: isDisbursementComplete(),
          completionPercent: getCompletionPercent('disbursement'),
          message: purchaseDoneForDisb ? undefined : 'Please complete the Purchase Hub first'
        }
      
      case 'irs':
        const disbDone = isDisbursementComplete()
        return {
          canAccess: disbDone,
          isComplete: false,
          completionPercent: 0,
          message: disbDone ? undefined : 'Please complete the Disbursement Hub first'
        }
      
      default:
        return {
          canAccess: false,
          isComplete: false,
          completionPercent: 0
        }
    }
  }, [hubType, salesData.data, purchaseData.data, exchangeData.data, user?.firstName, user?.lastName, user?.address, user?.ssn, user?.isIdentityVerified, isIdentityVerified])

  return hubAccess
}

